'use strict';

// LPリーグ運営カルーセル（無限ループ版）。
// scroll-snap + 前後クローンによる無限ループ + ドット + 矢印 + activeスケール + キーボード操作。
// スライドセットを前後に複製することで両隣のカードが常に覗くため、
// 最初の表示で左側が空かず、端でも途切れずにループする。
// prefers-reduced-motion 尊重（スムーズスクロール無効）。
//
// 期待するマークアップ:
//   [data-lp-carousel]
//     .lp-carousel__arrow--prev / .lp-carousel__arrow--next
//     .lp-carousel__track > .lp-carousel__slide ...
//     .lp-carousel__dots（ドットはJSが生成: button.lp-carousel__dot, 選択時 .is-on）
(function() {
  const prefersReduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initCarousel(wrap) {
    const track = wrap.querySelector('.lp-carousel__track');
    const dotsBox = wrap.querySelector('.lp-carousel__dots');
    if (!track || !dotsBox) return;
    if (track.dataset.loopReady === '1') return; // turbolinks再初期化での二重クローン防止

    const realSlides = Array.from(track.querySelectorAll('.lp-carousel__slide'));
    const realCount = realSlides.length;
    if (realCount === 0) return;

    const prev = wrap.querySelector('.lp-carousel__arrow--prev');
    const next = wrap.querySelector('.lp-carousel__arrow--next');

    // ── 無限ループ用にスライドセットを前後へ複製: [clone set][real set][clone set] ──
    // 中央の本物セットを基準（canonical）とし、端のクローンに着地したら静かに中央へ巻き戻す。
    function cloneSlide(s) {
      const clone = s.cloneNode(true);
      clone.classList.add('lp-carousel__slide--clone');
      clone.setAttribute('aria-hidden', 'true'); // スクリーンリーダーでの重複読み上げを防ぐ
      return clone;
    }
    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();
    realSlides.forEach((s) => before.appendChild(cloneSlide(s)));
    realSlides.forEach((s) => after.appendChild(cloneSlide(s)));
    track.insertBefore(before, realSlides[0]);
    track.appendChild(after);

    // 複製後の全スライド（本物は中央セット = index realCount .. realCount*2-1）
    const slides = Array.from(track.querySelectorAll('.lp-carousel__slide'));
    const canonicalStart = realCount;       // 本物先頭のDOM index
    const canonicalEnd = realCount * 2 - 1; // 本物末尾のDOM index

    // スライド中央のトラック内座標（scroll-snap-align: center に合わせて中央基準で計算する）
    const slideCenter = (s) => s.offsetLeft + s.offsetWidth / 2;

    // DOM index を本物のインデックス(0..realCount-1)へ写像
    const realIndexOf = (i) => ((i % realCount) + realCount) % realCount;

    function leftFor(i) {
      const target = Math.max(0, Math.min(slides.length - 1, i));
      const left = slideCenter(slides[target]) - track.clientWidth / 2;
      const maxLeft = track.scrollWidth - track.clientWidth;
      return Math.max(0, Math.min(maxLeft, left));
    }

    // ドットは本物の枚数ぶんだけ生成
    // スライドラベルはdata属性経由でロケール対応（data-slide-label）
    const slideLabel = wrap.dataset.slideLabel || '';
    dotsBox.textContent = '';
    realSlides.forEach((s, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'lp-carousel__dot';
      dot.setAttribute('aria-label', `${slideLabel} ${i + 1} / ${realCount}`);
      dot.addEventListener('click', () => goToReal(i));
      dotsBox.appendChild(dot);
    });
    const dots = Array.from(dotsBox.children);

    function currentIndex() {
      // ビューポート中央に最も近いスライド（クローン含む全スライド）のDOM indexを返す
      const viewportCenter = track.scrollLeft + track.clientWidth / 2;
      let best = canonicalStart;
      let bestDist = Infinity;
      slides.forEach((s, i) => {
        const d = Math.abs(slideCenter(s) - viewportCenter);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      });
      return best;
    }

    function go(i, behavior) {
      track.scrollTo({
        left: leftFor(i),
        behavior: behavior || (prefersReduce() ? 'auto' : 'smooth')
      });
    }

    // ドット/任意の本物スライドへ移動する際は、現在地から最も近いクローン位置を選ぶ
    function goToReal(ri) {
      const cur = currentIndex();
      const candidates = [ri, ri + realCount, ri + realCount * 2];
      let best = candidates[0];
      candidates.forEach((c) => {
        if (Math.abs(c - cur) < Math.abs(best - cur)) best = c;
      });
      go(best);
    }

    function sync() {
      const idx = currentIndex();
      const ri = realIndexOf(idx);
      dots.forEach((d, j) => d.classList.toggle('is-on', j === ri));
      slides.forEach((s, j) => s.classList.toggle('is-active', j === idx));
      // 無限ループのため矢印は常に有効（disabled制御は行わない）
    }

    // 端のクローンに着地したら、等価な本物スライドへ瞬間移動して巻き戻す。
    // 移動先は見た目が同一なのでチラつかない。
    function maybeWrap() {
      const idx = currentIndex();
      let delta = 0;
      if (idx < canonicalStart) delta = realCount;
      else if (idx > canonicalEnd) delta = -realCount;
      if (delta === 0) return;
      const fromCenter = slideCenter(slides[idx]);
      const toCenter = slideCenter(slides[idx + delta]);
      track.scrollLeft += toCenter - fromCenter;
    }

    if (prev) prev.addEventListener('click', () => go(currentIndex() - 1));
    if (next) next.addEventListener('click', () => go(currentIndex() + 1));

    let raf;
    let idleTimer;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(sync);
      // スクロールが落ち着いたタイミングでのみ巻き戻す（スムーズスクロール中は発火しない）
      clearTimeout(idleTimer);
      idleTimer = setTimeout(maybeWrap, 120);
    }, { passive: true });

    // リサイズ時は現在の本物スライドを中央セット内で中央維持する
    window.addEventListener('resize', () => {
      const ri = realIndexOf(currentIndex());
      track.scrollLeft = leftFor(canonicalStart + ri);
      sync();
    });

    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(currentIndex() + 1);
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(currentIndex() - 1);
      }
    });

    // 初期表示は本物先頭を中央に。前方クローンが左に覗くため左側が空かない。
    track.dataset.loopReady = '1';
    track.scrollLeft = leftFor(canonicalStart);
    sync();
  }

  function init() {
    document.querySelectorAll('[data-lp-carousel]').forEach(initCarousel);
  }

  document.addEventListener('turbolinks:load', init);
})();
